import { Check, Mail, User as UserIcon } from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Button } from '../../../../components/atoms/button/button';
import { Input } from '../../../../components/atoms/input/input';
import { Modal } from '../../../../components/molecules/modal/modal';
import {
  UpdateUsuarioPayload,
  UsuarioInterno,
} from '../../../../types/usuario';

interface Props {
  isOpen: boolean;
  user: UsuarioInterno | null;
  loading?: boolean;
  errorMessage?: string | null;
  onConfirm: (payload: UpdateUsuarioPayload) => Promise<unknown> | void;
  onClose: () => void;
}

interface FieldErrors {
  name?: string;
  email?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (values: { name: string; email: string }): FieldErrors => {
  const errors: FieldErrors = {};
  if (!values.name.trim()) errors.name = 'Nome é obrigatório';
  else if (values.name.trim().length < 2) errors.name = 'Nome muito curto';

  if (!values.email.trim()) errors.email = 'E-mail é obrigatório';
  else if (!EMAIL_RE.test(values.email.trim())) errors.email = 'E-mail inválido';

  return errors;
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FieldError = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.danger};
`;

const ErrorAlert = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
`;

export const EditUserModal = ({
  isOpen,
  user,
  loading,
  errorMessage,
  onConfirm,
  onClose,
}: Props) => {
  const [errors, setErrors] = useState<FieldErrors>({});
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && user) {
      if (nameRef.current) nameRef.current.value = user.name;
      if (emailRef.current) emailRef.current.value = user.email;
      setErrors({});
    }
  }, [isOpen, user]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) return;
    const values = {
      name: nameRef.current?.value ?? '',
      email: emailRef.current?.value ?? '',
    };
    const fieldErrors = validate(values);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    const payload: UpdateUsuarioPayload = {};
    const newName = values.name.trim();
    const newEmail = values.email.trim();
    if (newName !== user.name) payload.name = newName;
    if (newEmail !== user.email) payload.email = newEmail;

    await onConfirm(payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Editar dados do usuário"
      description="Atualize nome e/ou e-mail. Senha e permissões são alteradas em outros fluxos."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="edit-user-form"
            loading={loading}
            startIcon={!loading ? <Check size={14} /> : undefined}
            data-testid="edit-user-action"
          >
            {loading ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <Body as="form" id="edit-user-form" onSubmit={handleSubmit} noValidate>
        <Field>
          <FieldLabel htmlFor="edit-user-name">Nome</FieldLabel>
          <Input
            ref={nameRef}
            id="edit-user-name"
            name="name"
            defaultValue={user?.name ?? ''}
            hasError={Boolean(errors.name)}
            startIcon={<UserIcon size={16} />}
            aria-invalid={Boolean(errors.name)}
          />
          {errors.name && <FieldError>{errors.name}</FieldError>}
        </Field>

        <Field>
          <FieldLabel htmlFor="edit-user-email">E-mail</FieldLabel>
          <Input
            ref={emailRef}
            id="edit-user-email"
            name="email"
            type="email"
            defaultValue={user?.email ?? ''}
            hasError={Boolean(errors.email)}
            startIcon={<Mail size={16} />}
            aria-invalid={Boolean(errors.email)}
          />
          {errors.email && <FieldError>{errors.email}</FieldError>}
        </Field>

        {errorMessage && <ErrorAlert role="alert">{errorMessage}</ErrorAlert>}
      </Body>
    </Modal>
  );
};
